import React, { useState, useEffect, useCallback, useRef } from 'react';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import SearchableSelect from '../../Common/SearchableSelect';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import useClosedPeriod from '../../../hooks/useClosedPeriod';

const SupplierReturnNoteForm = ({ returnNote, onSuccess, onCancel }) => {
    const docsRef = useRef(null);
    const [formData, setFormData] = useState({
        supplier: '',
        grn: '', // Back to single GRN
        location: '',
        returnDate: new Date().toISOString().split('T')[0],
        reason: '',
        notes: '',
        items: []
    });

    const [availableGRNs, setAvailableGRNs] = useState([]);
    const [locations, setLocations] = useState([]);
    const [returnTypes, setReturnTypes] = useState([]);
    const [selectedGRN, setSelectedGRN] = useState(null); // Back to single GRN
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // Generate return reasons from loaded return types or use fallback
    const returnReasons = returnTypes.length > 0
        ? returnTypes.map(type => ({ value: type._id, label: type.name }))
        : [];

    // Initialize form data
    const { minDate, dateError } = useClosedPeriod();
    const closedError = dateError(formData.returnDate);

    useEffect(() => {
        if (returnNote) {

            // Handle items from either 'items' or 'returnItems' field
            const items = returnNote.returnItems || returnNote.items || [];
            const formattedItems = items.map((item, index) => {
                // Calculate remaining quantity for display in edit mode
                const grnQuantity = item.grnQuantity || item.receivedQuantity || 0;
                const alreadyReturned = item.alreadyReturned || 0;
                const currentReturn = item.returnQuantity || 0;

                // For editing: Backend might include current return in alreadyReturned
                // So we calculate: total_received - (already_returned - current_return)
                const otherReturns = Math.max(0, alreadyReturned - currentReturn);
                const remainingQuantity = grnQuantity - otherReturns; // Available including current return

                return {
                    item: item.item?._id || item.item || null,
                    assetId: item.assetId || null,
                    itemName: item.itemName || item.item?.name || `Item ${index + 1}`,
                    itemCode: (item.itemCode || item.item?.code || `ITEM-${index + 1}`) ? String(item.itemCode || item.item?.code || `ITEM-${index + 1}`).trim() : `ITEM-${index + 1}`,
                    receivedQuantity: grnQuantity, // Total received from GRN
                    availableForReturn: remainingQuantity, // Available for return (including current return for editing)
                    alreadyReturned: otherReturns, // Other returns (excluding current being edited)
                    returnQuantity: currentReturn, // Current return quantity
                    reason: item.itemReturnReason || item.reason || '',
                    unitPrice: item.unitPrice || 0,
                    totalPrice: item.totalValue || (currentReturn * (item.unitPrice || 0)) || 0,
                    unit: (item.unit || item.item?.unit || 'PCS') ? String(item.unit || item.item?.unit || 'PCS').trim() : 'PCS',
                    // Additional fields for tracking
                    batchNumber: item.batchNumber || '',
                    serialNumbers: item.serialNumbers || [],
                    expiryDate: item.expiryDate || null
                };
            });

            setFormData({
                supplier: returnNote.supplier?._id || returnNote.supplier || '',
                grn: returnNote.grn?._id || returnNote.grn || '',
                location: returnNote.location?._id || returnNote.location || '',
                returnDate: returnNote.returnDate ? new Date(returnNote.returnDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                reason: returnNote.reason || '',
                notes: returnNote.notes || '',
                items: formattedItems
            });

        } else {
        }
    }, [returnNote]);

    // Stable primitive IDs for comparing against the (possibly-populated) returnNote refs
    const returnNoteSupplierId = returnNote?.supplier?._id || returnNote?.supplier || '';
    const returnNoteGrnId = returnNote?.grn?._id || returnNote?.grn || '';

    // Fetch initial data
    useEffect(() => {
        fetchInitialData();
    }, []);

    // Fetch GRNs when supplier changes
    useEffect(() => {
        if (formData.supplier && formData.supplier !== returnNoteSupplierId) {
            const supplierId = formData.supplier?._id || formData.supplier;
            fetchAvailableGRNs(supplierId);
        }
    }, [formData.supplier, returnNoteSupplierId]);

    // Fetch GRN details when GRN is selected
    useEffect(() => {
        if (formData.grn && formData.grn !== returnNoteGrnId) {
            // Check if we already have this GRN data from availableGRNs
            const existingGRN = availableGRNs.find(grn => grn._id === formData.grn);
            if (existingGRN && existingGRN.items && existingGRN.items.length > 0) {
                setSelectedGRN(existingGRN);

                const initialItems = existingGRN.items.map((item, index) => {
                    const grnQuantity = item.quantity || item.receivedQuantity || 0;
                    const alreadyReturned = item.alreadyReturned || 0;
                    const availableForReturn = item.availableForReturn !== undefined
                        ? item.availableForReturn
                        : Math.max(0, grnQuantity - alreadyReturned);

                    return {
                        item: item.item?._id || item.item || null,
                        assetId: item.assetId || null,
                        itemName: item.item?.name || item.itemName || `Item ${index + 1}`,
                        itemCode: (item.item?.code || item.itemCode || `ITEM-${index + 1}`) ? String(item.item?.code || item.itemCode || `ITEM-${index + 1}`).trim() : `ITEM-${index + 1}`,
                        receivedQuantity: grnQuantity,
                        availableForReturn: availableForReturn,
                        alreadyReturned: alreadyReturned,
                        returnQuantity: 0,
                        reason: '',
                        unitPrice: item.unitPrice || 0,
                        totalPrice: 0,
                        unit: (item.unit || item.item?.unit || 'PCS') ? String(item.unit || item.item?.unit || 'PCS').trim() : 'PCS'
                    };
                });

                setFormData(prev => ({
                    ...prev,
                    items: initialItems,
                    location: existingGRN.location?._id || prev.location
                }));
            } else {
                // Fetch detailed GRN data - ensure we pass ID not object
                const grnId = formData.grn?._id || formData.grn;
                fetchGRNDetails(grnId);
            }
        } else if (!formData.grn) {
            // On the very first mount for an edit, this effect can run with the
            // pre-init `formData.grn` ('') still in its closure — before the
            // "Initialize form data" effect's update has been applied — even
            // though that effect already queued the correct grn/items. Skip the
            // wipe in that case so it doesn't clobber the items about to be set.
            if (returnNoteGrnId) return;
            setSelectedGRN(null);
            setFormData(prev => ({ ...prev, items: [] }));
        }
    }, [formData.grn, returnNoteGrnId, availableGRNs.length]);

    const fetchInitialData = useCallback(async () => {
        try {
            setLoading(true);

            const [locationsResponse, returnTypesResponse] = await Promise.all([
                inventoryApi.getWarehouses(),
                inventoryApi.getReturnTypes()
            ]);

            setLocations(locationsResponse.data || []);
            setReturnTypes(returnTypesResponse.data || []);

            // If editing, fetch available GRNs for the supplier
            if (returnNote?.supplier) {
                const supplierId = returnNote.supplier?._id || returnNote.supplier;
                await fetchAvailableGRNs(supplierId);
            }

            // If editing, fetch GRN details — preserve the return quantities/reasons
            // already loaded from returnNote.returnItems rather than zeroing them out
            if (returnNote?.grn) {
                const grnId = returnNote.grn?._id || returnNote.grn;
                await fetchGRNDetails(grnId, { preserveReturns: true });
            }
        } catch (err) {
            console.error('❌ Error fetching initial data:', err);
            let errorMessage = 'Failed to load form data';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            }
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [returnNote?.supplier, returnNote?.grn]);

    const fetchAvailableGRNs = useCallback(async (supplierId) => {
        try {
            if (!supplierId) {
                return;
            }
            const response = await inventoryApi.getGRNsAvailableForReturns(supplierId);
            setAvailableGRNs(response.data || []);
        } catch (err) {
            console.error('❌ Error fetching available GRNs:', err);
            toast.error(err?.response?.data?.message || 'Failed to load available GRNs for supplier');
        }
    }, []);

    const fetchGRNDetails = useCallback(async (grnId, { preserveReturns = false } = {}) => {
        try {
            if (!grnId) {
                return;
            }
            const response = await inventoryApi.getGoodReceivedNote(grnId);
            const grn = response.data;

            setSelectedGRN(grn);

            // Use lineItems from the GRN (standard GRN API returns lineItems, not items)
            const itemsArray = grn.items || grn.lineItems || [];

            if (itemsArray.length === 0) {
                console.warn('⚠️ No items found in GRN');
                toast.error('No items found in the selected GRN');
                return;
            }

            setFormData(prev => {
                // When initializing an existing return note, preserve the return
                // quantities/reasons already loaded from returnNote.returnItems
                // instead of zeroing them out with fresh GRN line-item data.
                // Keyed by item id or assetId, whichever the row uses.
                const existingByItemId = preserveReturns
                    ? new Map((prev.items || []).map(it => [String(it.item || it.assetId), it]))
                    : new Map();

                const initialItems = itemsArray.map((item, index) => {
                    const grnQuantity = item.quantity || item.receivedQuantity || item.acceptedQuantity || item.deliveredQuantity || 0;
                    const alreadyReturned = item.alreadyReturned || 0;
                    const availableForReturn = item.availableForReturn !== undefined
                        ? item.availableForReturn
                        : Math.max(0, grnQuantity - alreadyReturned);
                    const isAsset = !item.item && !!item.assetId;
                    const itemId = item.item?._id || item.item || null;
                    const assetId = item.assetId || null;
                    const existing = existingByItemId.get(String(itemId || assetId));
                    const returnQuantity = existing?.returnQuantity || 0;
                    const unitPrice = item.unitPrice || 0;

                    return {
                        item: itemId,
                        assetId,
                        itemName: isAsset
                            ? (item.assetName || item.itemName || `Asset ${index + 1}`)
                            : (item.item?.name || item.itemName || `Item ${index + 1}`),
                        itemCode: (isAsset
                            ? (item.assetCode || item.itemCode || `ASSET-${index + 1}`)
                            : (item.item?.code || item.itemCode || `ITEM-${index + 1}`)
                        ).toString().trim(),
                        receivedQuantity: grnQuantity,
                        availableForReturn: availableForReturn,
                        alreadyReturned: alreadyReturned,
                        returnQuantity,
                        reason: existing?.reason || '',
                        unitPrice,
                        totalPrice: existing?.totalPrice || (returnQuantity * unitPrice),
                        unit: (item.unit || item.item?.unit || 'PCS') ? String(item.unit || item.item?.unit || 'PCS').trim() : 'PCS'
                    };
                });

                return {
                    ...prev,
                    items: initialItems,
                    location: grn.location?._id || prev.location
                };
            });
        } catch (err) {
            console.error('❌ Error fetching GRN details:', err);
            toast.error(err?.response?.data?.message || 'Failed to load GRN details');
        }
    }, []);



    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };

        // Recalculate total price for quantity changes
        if (field === 'returnQuantity') {
            updatedItems[index].totalPrice = value * updatedItems[index].unitPrice;
        }

        setFormData(prev => ({
            ...prev,
            items: updatedItems
        }));

        // Clear validation error for this item
        if (validationErrors[`items.${index}.${field}`]) {
            setValidationErrors(prev => ({
                ...prev,
                [`items.${index}.${field}`]: null
            }));
        }
    };

    const validateForm = () => {
        const errors = {};

        // Required fields
        if (!formData.supplier) errors.supplier = 'Supplier is required';
        if (!formData.grn) errors.grn = 'GRN is required';
        if (!formData.location) errors.location = 'Location is required';
        if (!formData.returnDate) errors.returnDate = 'Return date is required';
        if (!formData.reason) errors.reason = 'Return reason is required';

        // Items validation
        if (!formData.items || formData.items.length === 0) {
            errors.items = 'At least one item must be returned';
        } else {
            let hasItemsToReturn = false;
            formData.items.forEach((item, index) => {
                if (item.returnQuantity > 0) {
                    hasItemsToReturn = true;

                    // Check if return quantity exceeds available quantity
                    const availableForReturn = getAvailableQuantity(item);

                    if (item.returnQuantity > availableForReturn) {
                        errors[`items.${index}.returnQuantity`] = `Cannot return more than ${availableForReturn} units`;
                    }

                    if (!item.reason) {
                        errors[`items.${index}.reason`] = 'Reason is required for items being returned';
                    }
                }
            });

            if (!hasItemsToReturn) {
                errors.items = 'At least one item must have a return quantity greater than 0';
            }
        }

        setValidationErrors(errors);
        const isValid = Object.keys(errors).length === 0;
        return isValid;
    };



    const handleSubmit = async (e, shouldClose = true) => {
        if (e) e.preventDefault();


        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);

            // Filter items with return quantity > 0 and map to expected backend structure
            const itemsToReturn = formData.items
                .filter(item => item.returnQuantity > 0)
                .map(item => ({
                    // Required fields from schema - ensure they exist and are not empty
                    // Exactly one of item/assetId is set, depending on whether this row
                    // came from an INVENTORY or FIXED_ASSET source GRN.
                    item: item.assetId ? undefined : item.item,
                    assetId: item.assetId || undefined,
                    itemCode: (item.itemCode && typeof item.itemCode === 'string' && item.itemCode.trim()) || `ITEM-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                    itemName: item.itemName || 'Unknown Item',
                    sourceGrn: formData.grn,
                    sourceGrnNumber: selectedGRN?.grnNumber || '',
                    grnQuantity: item.receivedQuantity || item.availableForReturn || 0,
                    returnQuantity: Number(item.returnQuantity) || 0,
                    unitPrice: Number(item.unitPrice) || 0,
                    totalValue: Number(item.totalPrice) || (Number(item.returnQuantity) * Number(item.unitPrice)) || 0,
                    unit: (item.unit && typeof item.unit === 'string' && item.unit.trim()) || 'PCS',
                    itemReturnReason: item.reason || '',
                    // Additional fields for updates
                    ...(returnNote && {
                        batchNumber: item.batchNumber || '',
                        serialNumbers: item.serialNumbers || [],
                        expiryDate: item.expiryDate || null
                    })
                }));


            if (itemsToReturn.length === 0) {
                toast.error('Please select at least one item to return with quantity > 0');
                return;
            }

            const submitData = {
                supplier: formData.supplier,
                grn: formData.grn,
                location: formData.location,
                returnDate: formData.returnDate,
                reason: formData.reason,
                notes: formData.notes,
                returnItems: itemsToReturn
            };


            let response;
            if (returnNote) {
                response = await inventoryApi.updateSupplierReturnNote(returnNote._id, submitData);
            } else {
                response = await inventoryApi.createSupplierReturnNote(submitData);
                await docsRef.current?.uploadPending(response?.data?._id);
            }

            if (shouldClose) {
                onSuccess();
            }
        } catch (err) {
            console.error('❌ Error saving return note:', err);
            console.error('❌ Error response:', err.response?.data);
            console.error('❌ Error status:', err.response?.status);
            console.error('❌ Full error object:', err);

            let errorMessage = 'Failed to save return note';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                errorMessage = err.message;
            }

            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        onCancel();
    };

    const getAvailableQuantity = (item) => {
        // Use the backend-calculated available quantity if available, otherwise calculate it
        return item.availableForReturn !== undefined ? item.availableForReturn : (item.receivedQuantity - (item.alreadyReturned || 0));
    };

    const getTotalReturnValue = () => {
        return formData.items.reduce((total, item) => total + item.totalPrice, 0);
    };

    const canEditReturnNote = (returnNote) => {
        // Allow editing for new returns (no returnNote) or specific statuses
        if (!returnNote) return true;
        return ['pending_approval', 'rejected', null, undefined].includes(returnNote.status);
    };

    const canSubmitForm = () => {
        const hasBasicInfo = formData.supplier && formData.grn && formData.location && formData.returnDate && formData.reason;
        const hasItemsToReturn = formData.items.some(item => item.returnQuantity > 0);
        const canEdit = canEditReturnNote(returnNote);
        return hasBasicInfo && hasItemsToReturn && selectedGRN && canEdit;
    };

    const editable = canEditReturnNote(returnNote);

    const inputCls = (hasError) =>
        `w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'}`;

    return (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
            <div className="flex items-center justify-center min-h-full px-4 py-6">
                <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">

                    {/* Data loading overlay */}
                    {loading && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center gap-3">
                            <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            <span className="text-sm font-medium text-indigo-600">Loading return note data…</span>
                        </div>
                    )}

                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-sm font-semibold text-white">
                                    {returnNote ? 'Edit Return Note' : 'New Supplier Return Note'}
                                </h3>
                                {returnNote && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${returnNote.status === 'pending_approval' ? 'bg-yellow-500/20 text-yellow-100' :
                                        returnNote.status === 'approved' ? 'bg-green-500/20 text-green-100' :
                                            returnNote.status === 'rejected' ? 'bg-red-500/20 text-red-100' :
                                                'bg-white/20 text-white'
                                        }`}>
                                        {returnNote.status === 'pending_approval' ? 'Pending Approval' :
                                            returnNote.status === 'approved' ? 'Approved' :
                                                returnNote.status === 'rejected' ? 'Rejected' :
                                                    returnNote.status === 'processed' ? 'Processed' :
                                                        returnNote.status === 'completed' ? 'Completed' :
                                                            returnNote.status || 'Draft'}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={handleCancel}
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="bg-white">
                        <form id="supplier-return-form" onSubmit={handleSubmit} className="px-4 pt-3 pb-2">

                            {/* Error Summary */}
                            {error && (
                                <div className="mb-2 bg-red-50 border border-red-200 rounded p-2">
                                    <p className="text-xs text-red-700">{error}</p>
                                </div>
                            )}

                            {/* Header Fields — compact 5-col row */}
                            <div className="grid grid-cols-5 gap-2 mb-2">
                                {/* Supplier */}
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Supplier <span className="text-red-500">*</span></label>
                                    <SearchableInventorySupplierDropdown usableFor="goods"
                                        value={formData.supplier}
                                        onChange={(id) => {
                                            handleInputChange('supplier', id);
                                            handleInputChange('grn', ''); // Reset GRN when supplier changes
                                            setSelectedGRN(null);
                                            setFormData(prev => ({ ...prev, items: [] }));
                                        }}
                                        placeholder="Select supplier"
                                        error={!!validationErrors.supplier}
                                        disabled={!editable}
                                        menuPlacement="down"
                                    />
                                    {validationErrors.supplier && <p className="text-xs text-red-500 mt-0.5">{validationErrors.supplier}</p>}
                                    {!formData.supplier && !validationErrors.supplier && (
                                        <p className="text-[10px] text-gray-400 mt-0.5">Select supplier to view available GRNs</p>
                                    )}
                                </div>

                                {/* GRN */}
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Good Received Note <span className="text-red-500">*</span></label>
                                    <SearchableSelect
                                        value={formData.grn}
                                        onChange={(e) => handleInputChange('grn', e.target.value)}
                                        disabled={!formData.supplier || !editable}
                                        options={availableGRNs.map(grn => ({
                                            value: grn._id,
                                            label: `${grn.grnNumber} - ${new Date(grn.receiptDate).toLocaleDateString()} (${grn.totalValue?.toFixed(2) || '0.00'})`
                                        }))}
                                        placeholder={formData.supplier ? 'Select GRN...' : 'Select supplier first'}
                                        error={validationErrors.grn || ''}
                                        size="sm"
                                    />
                                    {formData.supplier && availableGRNs.length === 0 && (
                                        <p className="text-[10px] text-amber-600 mt-0.5">No GRNs available for return from this supplier</p>
                                    )}
                                </div>

                                {/* Return Date */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Return Date <span className="text-red-500">*</span></label>
                                    <input
                                        type="date"
                                        value={formData.returnDate}
                                        min={minDate}
                                        onChange={(e) => handleInputChange('returnDate', e.target.value)}
                                        max={new Date().toISOString().split('T')[0]}
                                        disabled={!editable}
                                        className={inputCls(!!validationErrors.returnDate)}
                                    />
                                    {validationErrors.returnDate && <p className="text-xs text-red-500 mt-0.5">{validationErrors.returnDate}</p>}
{closedError && <p className="text-xs text-red-600 mt-0.5">{closedError}</p>}
                                </div>
                            </div>

                            {/* Location + Reason row */}
                            <div className="grid grid-cols-5 gap-2 mb-3">
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Location <span className="text-red-500">*</span></label>
                                    <SearchableSelect
                                        value={formData.location}
                                        onChange={(e) => handleInputChange('location', e.target.value)}
                                        disabled={!selectedGRN || !editable}
                                        options={locations.map(location => ({
                                            value: location._id,
                                            label: location.name
                                        }))}
                                        placeholder="Select location..."
                                        error={validationErrors.location || ''}
                                        size="sm"
                                    />
                                    {validationErrors.location && <p className="text-xs text-red-500 mt-0.5">{validationErrors.location}</p>}
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Return Reason <span className="text-red-500">*</span></label>
                                    <SearchableSelect
                                        value={formData.reason}
                                        onChange={(e) => handleInputChange('reason', e.target.value)}
                                        options={returnReasons}
                                        placeholder="Select return reason..."
                                        error={validationErrors.reason || ''}
                                        size="sm"
                                        disabled={!editable}
                                    />
                                    {validationErrors.reason && <p className="text-xs text-red-500 mt-0.5">{validationErrors.reason}</p>}
                                </div>
                            </div>

                            {/* Items Section — styled like PurchaseOrderForm line items */}
                            <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
                                <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
                                    <span className="text-xs font-semibold text-white">
                                        Return Items ({formData.items.length})
                                    </span>
                                    <span className="text-[10px] text-indigo-200 font-normal">
                                        Total: Rs {getTotalReturnValue().toFixed(2)}
                                    </span>
                                </div>

                                <div className="max-h-[36vh] overflow-y-auto">
                                    {!selectedGRN || formData.items.length === 0 ? (
                                        <div className="px-3 py-8 text-center text-xs text-gray-400">
                                            Select a supplier and GRN to load return items.
                                        </div>
                                    ) : (
                                        <table className="min-w-max w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50 sticky top-0">
                                                <tr>
                                                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
                                                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">GRN Qty</th>
                                                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Remaining</th>
                                                    <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Return Qty</th>
                                                    <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Unit Price</th>
                                                    <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Total</th>
                                                    <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-100">
                                                {formData.items.map((item, index) => {
                                                    const availableQty = getAvailableQuantity(item);
                                                    return (
                                                        <tr key={`${item.item || item.assetId}-${index}`} className={item.returnQuantity > 0 ? 'bg-indigo-50/60' : 'hover:bg-gray-50'}>
                                                            <td className="px-2 py-1.5">
                                                                <div className="text-xs font-medium text-gray-900">{item.itemName}</div>
                                                                <div className="text-xs text-gray-500 font-mono">{item.itemCode}</div>
                                                            </td>
                                                            <td className="px-2 py-1.5 text-center text-xs">
                                                                <div className="text-gray-900 font-medium">{item.receivedQuantity}</div>
                                                                {item.alreadyReturned > 0 && (
                                                                    <div className="text-[10px] text-red-500">(-{item.alreadyReturned} returned)</div>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-1.5 text-center text-xs">
                                                                <div className={`font-medium ${availableQty > 0 ? 'text-green-600' : 'text-red-600'}`}>{availableQty}</div>
                                                            </td>
                                                            <td className="px-2 py-1.5">
                                                                <input
                                                                    type="number"
                                                                    value={item.returnQuantity}
                                                                    onChange={(e) => handleItemChange(index, 'returnQuantity', parseInt(e.target.value) || 0)}
                                                                    min="0"
                                                                    max={availableQty}
                                                                    disabled={availableQty === 0 || !editable}
                                                                    className={`${inputCls(!!validationErrors[`items.${index}.returnQuantity`])} w-20 text-center`}
                                                                />
                                                                {validationErrors[`items.${index}.returnQuantity`] && (
                                                                    <p className="text-[10px] text-red-500 mt-0.5">{validationErrors[`items.${index}.returnQuantity`]}</p>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-1.5 text-right text-xs text-gray-900">{item.unitPrice.toFixed(2)}</td>
                                                            <td className="px-2 py-1.5 text-right text-xs font-medium text-gray-900">{item.totalPrice.toFixed(2)}</td>
                                                            <td className="px-2 py-1.5 min-w-[160px]">
                                                                {item.returnQuantity > 0 && (
                                                                    <SearchableSelect
                                                                        value={item.reason}
                                                                        onChange={(e) => handleItemChange(index, 'reason', e.target.value)}
                                                                        disabled={!editable}
                                                                        options={returnReasons}
                                                                        placeholder="Select reason..."
                                                                        size="sm"
                                                                        error={validationErrors[`items.${index}.reason`] || ''}
                                                                    />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                {validationErrors.items && (
                                    <p className="text-xs text-red-500 px-3 py-1.5 border-t border-gray-100">{validationErrors.items}</p>
                                )}
                            </div>

                            {/* Notes — full width, under the items table */}
                            <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    rows={2}
                                    placeholder="Additional notes about the return…"
                                    disabled={!editable}
                                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                                />
                                {returnNote && !editable && (
                                    <p className="mt-1 text-[10px] text-gray-500 flex items-center">
                                        <svg className="w-3 h-3 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Return note is {returnNote.status.replace('_', ' ')} — cannot be modified
                                    </p>
                                )}
                            </div>

                            {/* Supporting documents */}
                            <ProcurementDocuments ref={docsRef} docType="supplier-return-notes" docId={returnNote?._id || null} className="mb-3" />

                            {/* Footer: Totals | Actions */}
                            <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-100">

                                {/* Totals Panel */}
                                <div className="w-56 border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                    <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600">
                                        <span className="text-[10px] font-bold text-indigo-100 uppercase tracking-wider">Total Return Value</span>
                                        <span className="text-sm font-black text-white">Rs {getTotalReturnValue().toFixed(2)}</span>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={submitting}
                                        className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    {editable && (
                                        <button
                                            type="submit"
                                            form="supplier-return-form"
                                            disabled={submitting || !canSubmitForm() || !!closedError}
                                            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                        >
                                            {submitting && (
                                                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                            )}
                                            {submitting ? 'Saving...' : (returnNote ? 'Update Return Note' : 'Create Return Note for Approval')}
                                        </button>
                                    )}
                                </div>
                            </div>

                        </form>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default SupplierReturnNoteForm;
